/**
 * ExceptionsPanel — the prominent, first thing reception sees.
 *
 * The board is exceptions-first: this panel surfaces only requests that demand a
 * human decision (unacknowledged / overdue / escalated) via
 * `useServiceRequestExceptions`. An empty panel is a SUCCESS state, not an error,
 * so it renders a calm EmptyState rather than nothing.
 */

import { useServiceRequestExceptions } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { EmptyState, ErrorState, LoadingState } from '@hostesa/ui';
import { CheckCircle2 } from 'lucide-react';
import { TaskCard } from './task-card';

export interface ExceptionsPanelProps {
  tenantId: string;
  hotelId: string;
}

export function ExceptionsPanel({ tenantId, hotelId }: ExceptionsPanelProps) {
  const { t } = useT('operations');
  const query = useServiceRequestExceptions(tenantId, hotelId);

  return (
    <section data-testid="operations-exceptions-panel" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="font-[var(--font-display)] text-lg font-medium tracking-tight text-[var(--color-charcoal)]">
          {t('exceptions.title')}
        </h2>
        <p className="text-sm text-[var(--color-stone-mid)]">{t('exceptions.description')}</p>
      </header>

      {query.isPending ? (
        <LoadingState
          title={t('states.loadingTitle')}
          data-testid="operations-exceptions-loading"
        />
      ) : query.isError ? (
        <ErrorState
          title={t('states.errorTitle')}
          description={t('states.errorDescription')}
          data-testid="operations-exceptions-error"
        />
      ) : query.data.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 />}
          title={t('exceptions.empty')}
          description={t('exceptions.emptyDescription')}
          data-testid="operations-exceptions-empty"
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="operations-exceptions-list">
          {query.data.map((task) => (
            <TaskCard key={task.id} task={task} tenantId={tenantId} hotelId={hotelId} />
          ))}
        </div>
      )}
    </section>
  );
}
